import { Button } from "@/components/ui/button";
import { useSession } from "@supabase/auth-helpers-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useRef, useState, memo } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Heart, Star, Dog, PlusCircle, InfoIcon } from "lucide-react";
import { getPrimaryImageUrl, getProductImageUrl } from "@/utils/imageUtils";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { 
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from "@/components/ui/hover-card";
import { useCart } from "@/hooks/use-cart";

interface ProductCardProps {
  id: string;
  title: string;
  price: number;
  image: string;
  rating: number;
  description?: string;
  brand?: string;
  is_preorder?: boolean;
  preorder_price?: number | null;
  preorder_release_date?: string | null;
  preorder_available_quantity?: number | null;
  reviews?: Review[];
  stock_count?: number | null;
  sub_category?: string;
  product_images?: Array<{id: string, image_url: string, is_primary: boolean}> | null;
}

interface Review {
  id: string;
  rating: number;
  review_text: string | null;
  created_at: string;
  profiles: {
    first_name: string | null;
  } | null;
}

const COLORS = [
  "bg-red-500",
  "bg-blue-500",
  "bg-green-500",
  "bg-yellow-500",
  "bg-purple-500",
  "bg-pink-500",
  "bg-indigo-500",
];

const formatPrice = (price: number) => {
  return price.toFixed(2);
};

export const ProductCard = memo(({ 
  id, 
  title, 
  price, 
  image,
  rating,
  description = "No description available",
  brand = "Unknown Brand",
  is_preorder = false,
  preorder_price,
  preorder_release_date,
  preorder_available_quantity,
  reviews = [],
  stock_count = null,
  sub_category,
  product_images = null
}: ProductCardProps) => {
  const session = useSession();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();
  
  // Use the cart hook for both guest and logged-in users
  const { addItem, isLoading: cartLoading } = useCart();

  // Add query to check if user is admin
  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', session.user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  const { data: wishlistItem, refetch: refetchWishlist } = useQuery({
    queryKey: ['wishlist-status', id, session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data, error } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('product_id', id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  // Get stock count only when needed, with longer intervals
  const { data: currentProduct } = useQuery({
    queryKey: ['product-stock', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('stock_count, image_url')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching stock count:', error);
        throw error;
      }
      return data;
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchInterval: 60000, // Only refetch every minute instead of every 2 seconds
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
  });

  // Add reservation query with optimized intervals
  const { data: reservations } = useQuery({
    queryKey: ['cart-reservations', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cart_item_reservations')
        .select('quantity')
        .eq('product_id', id);
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 15000, // Consider data fresh for 15 seconds
    refetchInterval: 30000, // Refetch every 30 seconds instead of 2 seconds
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
  });

  // Calculate available stock by subtracting reservations
  const reservedQuantity = reservations?.reduce((total, res) => total + res.quantity, 0) || 0;
  const currentStockCount = currentProduct?.stock_count ?? stock_count;
  const availableStock = currentStockCount !== null ? currentStockCount - reservedQuantity : null;

  // Update stock checks to use availableStock
  const isOutOfStock = !is_preorder && availableStock !== null && availableStock <= 0;
  const isLastItem = !is_preorder && availableStock === 1;

  // Calculate average rating
  const hasReviews = reviews?.length > 0;
  const averageRating = hasReviews 
    ? Math.round(reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length)
    : 0;

  // Function to render stars
  const renderStars = () => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((index) => (
          <Star
            key={index}
            className={`h-4 w-4 ${
              hasReviews && index <= averageRating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-xs sm:text-sm text-gray-500">
          ({reviews?.length || 0} reviews)
        </span>
      </div>
    );
  };

  // Use getPrimaryImageUrl which handles both product_images array and legacy image_url
  const getDisplayImage = () => {
    if (product_images && product_images.length > 0) {
      // If we have product_images array, find the primary one or use the first
      const primaryImage = product_images.find(img => img.is_primary);
      const selectedImage = primaryImage ? primaryImage.image_url : product_images[0].image_url;
      // Process the image URL to ensure it's a full URL
      return getProductImageUrl(selectedImage);
    }
    // Fall back to the passed image prop if no product_images array
    return getProductImageUrl(image);
  };

  const isInWishlist = !!wishlistItem;

  const toggleWishlist = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!session) {
      toast({
        title: "Please login",
        description: "You need to be logged in to add items to wishlist",
        variant: "destructive",
      });
      navigate("/customer/login");
      return;
    }

    try {
      if (isInWishlist) {
        // Remove from wishlist
        const { error } = await supabase
          .from('wishlist_items')
          .delete()
          .eq('user_id', session.user.id)
          .eq('product_id', id);

        if (error) throw error;

        toast({
          title: "Removed from wishlist",
          description: "Item has been removed from your wishlist",
        });
      } else {
        // Add to wishlist
        const { error } = await supabase
          .from('wishlist_items')
          .insert({
            user_id: session.user.id,
            product_id: id
          });

        if (error) throw error;

        toast({
          title: "Added to wishlist",
          description: "Item has been added to your wishlist",
        });
      }

      // Refetch wishlist status
      refetchWishlist();
      // Also invalidate the wishlist-items query to update any wishlist components
      queryClient.invalidateQueries({ queryKey: ['wishlist-items'] });
    } catch (error) {
      console.error('Error updating wishlist:', error);
      toast({
        title: "Error",
        description: "Failed to update wishlist",
        variant: "destructive",
      });
    }
  };

  const createFloatingBall = (color: string, clickX: number, clickY: number) => {
    const ball = document.createElement('div');
    ball.className = `fixed w-3 h-3 rounded-full ${color} animate-float-to-cart`;
    
    // Use requestAnimationFrame to defer DOM queries until next frame
    requestAnimationFrame(() => {
      document.body.appendChild(ball);

      // Get cart icon position only when needed
      const cartIcon = document.querySelector('.cart-icon');
      if (!cartIcon) {
        if (document.body.contains(ball)) {
          document.body.removeChild(ball);
        }
        return;
      }

      // Cache rect to avoid multiple getBoundingClientRect calls
      const cartRect = cartIcon.getBoundingClientRect();
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      // Calculate the target position relative to viewport and scroll position
      const translateX = (cartRect.left + cartRect.width/2 - clickX + scrollX);
      const translateY = (cartRect.top + cartRect.height/2 - clickY + scrollY);

      // Set custom properties for the animation
      ball.style.setProperty('--translate-x', `${translateX}px`);
      ball.style.setProperty('--translate-y', `${translateY}px`);
      ball.style.left = `${clickX - scrollX}px`;
      ball.style.top = `${clickY - scrollY}px`;

      // Remove the ball after animation
      ball.addEventListener('animationend', () => {
        if (document.body.contains(ball)) {
          document.body.removeChild(ball);
        }
      });
    });
  };

  const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    if (isOutOfStock) {
      toast({
        title: "Out of Stock",
        description: "Sorry, this item is currently out of stock",
        variant: "destructive",
      });
      return;
    }

    try {
      const clickX = e.clientX;
      const clickY = e.clientY;
      const randomColor = COLORS[Math.floor(Math.random() * COLORS.length)];
      createFloatingBall(randomColor, clickX, clickY);

      // Create product object for cart
      const productForCart = {
        id,
        name: title,
        price: is_preorder && preorder_price ? preorder_price : price,
        image_url: getDisplayImage(),
        stock_count: currentStockCount ?? stock_count ?? 0,
        brand,
        category: sub_category,
        is_preorder: is_preorder ?? false,
      };

      // Add to cart using the hook (works for both guest and logged-in users)
      await addItem({ product: productForCart, quantity: 1 });
      
      toast({
        title: is_preorder ? "Pre-order placed" : "Added to cart",
        description: is_preorder ? "Your pre-order has been placed successfully" : "Item has been added to your cart",
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "Error",
        description: is_preorder ? "Failed to place pre-order" : "Failed to add item to cart",
        variant: "destructive",
      });
    }
  };

  const buttonText = () => {
    if (isOutOfStock) return "Out of Stock";
    if (isLastItem) return "Add Last Item";
    if (is_preorder) return "Pre-order Now";
    return "Add to Cart";
  };

  // Navigate to the product detail page
  const handleCardClick = () => {
    navigate(`/product/${id}`);
  };

  // Add aspect ratio wrapper for image to prevent layout shifts
  return (
    <div 
      className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow animate-fade-up cursor-pointer relative group flex flex-col h-full"
      onClick={handleCardClick}
      style={{ contain: "content" }}
    >
      {is_preorder && (
        <Badge 
          variant="secondary" 
          className="absolute top-4 left-4 z-10 text-xs sm:text-sm"
        >
          Pre-order
        </Badge>
      )}
      
      <div className="flex items-start justify-between gap-2 absolute top-4 right-4 z-10">
        <Button
          variant="ghost"
          size="icon"
          className="bg-white/80 hover:bg-white transition-colors"
          onClick={handleAddToCart}
          disabled={isOutOfStock || cartLoading || (is_preorder && preorder_available_quantity !== null && preorder_available_quantity <= 0)}
        >
          <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
        </Button>
        <button
          onClick={toggleWishlist}
          className="p-1.5 sm:p-2 rounded-full bg-white/80 hover:bg-white transition-colors group/heart"
        >
          <Heart 
            className={`h-4 w-4 sm:h-5 sm:w-5 transition-all duration-300 ${
              isInWishlist 
                ? 'fill-red-500 text-red-500 animate-sparkle' 
                : 'text-gray-500 group-hover/heart:animate-heart-fill'
            }`}
          />
        </button>
      </div>

      <div className="aspect-square w-full mb-3 sm:mb-4 relative overflow-hidden rounded-xl" style={{ contain: "layout style paint", contentVisibility: "auto" }}>
        {/* Home of Blu icon on image */}
        {sub_category === "Home of Blu" && (
          <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
              <div className="absolute bottom-2 left-2 z-30">
                <div className="bg-white rounded-full p-1.5 shadow-md cursor-help hover:scale-110 transition-transform">
                  <InfoIcon className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent 
              side="top" 
              align="start" 
              className="w-80 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 z-[300]"
              sideOffset={5}
              avoidCollisions={true}
              sticky="always"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex space-x-2">
                <Dog className="h-5 w-5 text-blue-500" />
                <div>
                  <h4 className="font-medium text-blue-700">Home of Blu Impact</h4>
                  <p className="text-sm text-blue-600 mt-1">
                    Home of Blu donates 1.5% of every toy they sell to support local dogs in need!
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        
        <OptimizedImage 
          src={getDisplayImage()}
          alt={title}
          className="w-full h-full"
          aspectRatio="square"
          priority={false}
        />
      </div>

      <div className="space-y-1.5 sm:space-y-2 flex-grow">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-gray-900 text-sm sm:text-base line-clamp-2 flex-1">{title}</h3>
          <div className="text-right ml-2">
            {is_preorder && preorder_price && (
              <>
                <span className="text-primary font-semibold text-sm sm:text-base">£{formatPrice(preorder_price)}</span>
                <p className="text-xs text-gray-500">Pre-order Price</p>
              </>
            )}
            {(!is_preorder || !preorder_price) && (
              <span className="text-primary font-semibold text-sm sm:text-base">£{formatPrice(price)}</span>
            )}
          </div>
        </div>
        {renderStars()}
        {is_preorder && (
          <p className="text-xs sm:text-sm text-gray-600">
            {preorder_available_quantity !== null && preorder_available_quantity > 0 
              ? `${preorder_available_quantity} pre-order slots available`
              : "Pre-order slots full"
            }
          </p>
        )}
        {/* Only show stock count to admin users */}
        {!is_preorder && availableStock !== null && availableStock > 0 && userProfile?.is_admin && (
          <p className="text-xs sm:text-sm text-gray-600">
            {availableStock} in stock
          </p>
        )}
      </div>
        
      <div className="mt-4">
        <Button 
          className={`w-full text-sm sm:text-base py-1.5 sm:py-2 ${
            is_preorder ? 'bg-secondary hover:bg-secondary/90' : ''
          }`}
          onClick={handleAddToCart}
          disabled={isOutOfStock || cartLoading || (is_preorder && preorder_available_quantity !== null && preorder_available_quantity <= 0)}
          variant={isOutOfStock ? "destructive" : is_preorder ? "secondary" : "default"}
        >
          {cartLoading ? "Adding..." : buttonText()}
        </Button>
      </div>
    </div>
  );
});

ProductCard.displayName = "ProductCard";
